// src/utils/stagingFiller.js
//
// The half of the staging handoff that runs on Sharon's website rather than in
// TES. An agent saves the bookmarklet once; from then on, clicking it on her
// open 17hats form types in the answers TES put on the clipboard.
//
// Why a bookmarklet and not a background POST: her form is behind a reCAPTCHA,
// which exists precisely to stop automated submissions. We fill; a human reads
// it over, clears the robot check, and presses Submit.
//
// fillStagingForm must stay SELF-CONTAINED — no imports, no module-scope
// helpers. buildFillerBookmarklet serializes it with Function.prototype
// .toString(), so anything it closes over would be undefined on her page.

/**
 * Fill Sharon's form from a TES payload.
 *
 * Refuses to guess. If a question is gone, or a dropdown no longer offers the
 * answer we intended, it says so instead of picking something approximate —
 * a wrong bid type costs more than a blank one.
 *
 * @returns {{filled:number,total:number,notFound:string[],changed:string[]}}
 */
export function fillStagingForm(doc, payload) {
  var fields = (payload && payload.fields) || [];
  var notFound = [];
  var changed = [];
  var filled = 0;

  // Her Squarespace contact page embeds the form from another origin, where no
  // script can reach it. Reporting "0 of 14 filled" there looks like a broken
  // tool; the honest answer is "you're on the wrong page".
  var wrongPage = false;
  if (!doc.querySelector('[name^="q27029"]')) {
    var frames = doc.querySelectorAll("iframe");
    for (var i = 0; i < frames.length; i++) {
      if ((frames[i].src || "").indexOf("17hats") !== -1) wrongPage = true;
    }
  }

  function setValue(el, v) {
    var proto =
      typeof HTMLTextAreaElement !== "undefined" &&
      el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : typeof HTMLSelectElement !== "undefined" &&
          el instanceof HTMLSelectElement
        ? HTMLSelectElement.prototype
        : HTMLInputElement.prototype;
    var desc = Object.getOwnPropertyDescriptor(proto, "value");
    if (desc && desc.set) desc.set.call(el, v);
    else el.value = v;
  }

  function fire(el) {
    ["input", "change"].forEach(function (type) {
      el.dispatchEvent(new Event(type, { bubbles: true }));
    });
  }

  fields.forEach(function (f) {
    var nodes = doc.querySelectorAll('[name="' + f.name + '"]');
    if (!nodes.length) {
      notFound.push(f.label);
      return;
    }

    if (f.type === "checkbox") {
      var wanted = Array.isArray(f.value) ? f.value : [f.value];
      var boxes = {};
      Array.prototype.forEach.call(nodes, function (n) {
        boxes[n.value] = n;
      });
      var absent = wanted.filter(function (w) {
        return !boxes[w];
      });
      if (absent.length) {
        changed.push(f.label);
        return;
      }
      // CLICK, don't set .checked. Her form is Knockout-bound and its checkbox
      // binding only updates the underlying list on a real click — setting
      // .checked ticks the box on screen while the model stays empty, so the
      // submission arrives with no rooms and her server rejects the whole
      // thing. This cost Henry a failed bid on 2026-08-02; the give-away was
      // that unticking and reticking a room by hand made it go through.
      Array.prototype.forEach.call(nodes, function (n) {
        var shouldBeOn = wanted.indexOf(n.value) !== -1;
        if (n.checked !== shouldBeOn) n.click();
      });
      filled += 1;
      return;
    }

    var el = nodes[0];

    if (el.tagName === "SELECT") {
      var match = null;
      Array.prototype.forEach.call(el.options, function (o) {
        if (
          (o.textContent || "").trim() === String(f.value).trim() ||
          (o.value || "").trim() === String(f.value).trim()
        ) {
          match = o;
        }
      });
      if (!match) {
        changed.push(f.label);
        return;
      }
      setValue(el, match.value);
      fire(el);
      filled += 1;
      return;
    }

    setValue(el, f.value);
    fire(el);
    filled += 1;
  });

  return {
    filled: filled,
    total: fields.length,
    notFound: notFound,
    changed: changed,
    wrongPage: wrongPage,
  };
}

/**
 * What state is the page in?
 *
 * Her form is a single-page app, so the tab exists for a moment before the
 * questions render. Filling into that gap reported "Filled 0 of 14", which
 * reads as a broken tool rather than "wait half a second" — so the bookmarklet
 * polls this until it says `ready`.
 *
 *   "ready"     — her questions are on the page, go ahead
 *   "wrongPage" — this is the page that embeds her form cross-origin
 *   "notReady"  — no questions yet; either still loading or not her form
 *
 * Self-contained: serialized into the bookmarklet.
 */
export function diagnosePage(doc) {
  if (doc.querySelector('[name^="q27029"]')) return "ready";
  var frames = doc.querySelectorAll("iframe");
  for (var i = 0; i < frames.length; i++) {
    if ((frames[i].src || "").indexOf("17hats") !== -1) return "wrongPage";
  }
  return "notReady";
}

/**
 * Pull the answers out of the tab's name and wipe it.
 *
 * TES opens her form with `window.open(url, JSON.stringify(payload))`, which
 * names the new tab after the payload. The name survives the cross-origin
 * navigation, needs no permission, and never appears in a URL, history entry,
 * referrer or server log the way a query string would.
 *
 * Wiping it afterwards matters: the name would otherwise persist in that tab
 * and be readable by whatever site the agent visits next.
 *
 * Self-contained — it is serialized into the bookmarklet alongside the filler.
 */
export function readStagingPayload(win) {
  var raw = (win && win.name) || "";
  if (!raw || raw.charAt(0) !== "{") return null;
  var parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!parsed || !parsed.fields || !parsed.fields.length) return null;
  win.name = "";
  return parsed;
}

/**
 * The one-time bookmark each agent drags to their bookmarks bar.
 *
 * Deliberately carries no listing data — the answers travel on the clipboard,
 * so the same saved bookmark works for every listing forever.
 */
export function buildFillerBookmarklet() {
  var body =
    "(function(){" +
    "var fill=" +
    fillStagingForm.toString() +
    ";" +
    "var readPayload=" +
    readStagingPayload.toString() +
    ";" +
    "var diagnosePage=" +
    diagnosePage.toString() +
    ";" +
    "function report(r){" +
    "var d=document.createElement('div');" +
    "d.style.cssText='position:fixed;z-index:2147483647;top:16px;right:16px;max-width:340px;padding:12px 14px;border-radius:10px;font:13px/1.5 -apple-system,Segoe UI,sans-serif;color:#fff;background:#1e293b;box-shadow:0 8px 24px rgba(0,0,0,.25)';" +
    "var ok=!r.notFound.length&&!r.changed.length;" +
    "var h;" +
    "if(r.wrongPage){" +
    "d.style.background='#7c2d12';" +
    "h='<b>Wrong page.</b><br>This is the page that embeds her form, and browsers won\\'t let anything reach inside it.<br><br>Open the form on its own using the button in TES, then click this bookmark there.'" +
    "}else if(r.noForm){" +
    "d.style.background='#7c2d12';" +
    "h='<b>Her form never appeared.</b><br>Waited 10 seconds on this page and found none of her questions.<br><br>Make sure this is the Register Redesign form tab TES opened, let it finish loading, then click the bookmark again.'" +
    "}else{" +
    "h='<b>Filled '+r.filled+' of '+r.total+' answers.</b>';" +
    "if(ok){h+='<br>Review it, clear the robot check, then Submit.'}" +
    "if(r.notFound.length){h+='<br><br>Not on her form any more:<br>'+r.notFound.join('<br>')}" +
    "if(r.changed.length){h+='<br><br>Her choices changed, fill these by hand:<br>'+r.changed.join('<br>')}" +
    "if(!ok){h+='<br><br>Finish those by hand before submitting.'}" +
    "}" +
    "d.innerHTML=h;" +
    "document.body.appendChild(d);" +
    "setTimeout(function(){d.remove()},15000);" +
    "}" +
    "function go(raw){" +
    "var p;try{p=JSON.parse(raw)}catch(e){alert('That does not look like a TES staging request. Copy it again from the listing file.');return}" +
    "if(!p||!p.fields||!p.fields.length){alert('That does not look like a TES staging request. Copy it again from the listing file.');return}" +
    "report(fill(document,p));" +
    "}" +
    "function ask(){var v=prompt('Paste the staging request you copied from TES');if(v){go(v)}}" +
    // Preferred route: the tab name TES set when it opened this form. Her site
    // denies clipboard-read, so that route always degrades to a paste box the
    // agent was never told to expect. Clipboard and prompt stay as fallbacks
    // for a tab opened by hand.
    // Wait for her single-page form to actually render before filling. Without
    // this, a click landing in the load gap reported "Filled 0 of 14".
    "function whenReady(cb){" +
    "var tries=0;" +
    "(function poll(){" +
    "var state=diagnosePage(document);" +
    "if(state==='ready'){cb(true);return}" +
    "if(state==='wrongPage'){cb(false,'wrongPage');return}" +
    "if(++tries>40){cb(false,'notReady');return}" +
    "setTimeout(poll,250)" +
    "})()" +
    "}" +
    "function start(p){" +
    "whenReady(function(ok,why){" +
    "if(ok){report(fill(document,p));return}" +
    "if(why==='wrongPage'){report({filled:0,total:p.fields.length,notFound:[],changed:[],wrongPage:true});return}" +
    "report({filled:0,total:p.fields.length,notFound:[],changed:[],noForm:true})" +
    "})" +
    "}" +
    "function go2(raw){var p;try{p=JSON.parse(raw)}catch(e){alert('That does not look like a TES staging request.');return}if(p&&p.fields&&p.fields.length){start(p)}}" +
    "var direct=readPayload(window);" +
    "if(direct){start(direct)}else{" +
    "try{" +
    "if(navigator.clipboard&&navigator.clipboard.readText){navigator.clipboard.readText().then(go2).catch(ask)}else{ask()}" +
    "}catch(e){ask()}" +
    "}" +
    "})()";
  return "javascript:" + encodeURIComponent(body);
}
